import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import sapi from "../lib/studentApi";
import { formatApiErrorDetail } from "../lib/api";
import { applyBrand, clearBrand } from "../design/brand";

const StudentAuthContext = createContext(null);
export const useStudentAuth = () => useContext(StudentAuthContext);

export function StudentAuthProvider({ children }) {
  const [student, setStudent] = useState(null); // null=checking, false=unauth, obj=authed
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      // Session lives in the httpOnly `student_access_token` cookie.
      const { data } = await sapi.get("/student/auth/me");
      setStudent(data);
    } catch {
      setStudent(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  // WL: theme the student portal from the school's branding; revert on logout.
  useEffect(() => {
    if (student && typeof student === "object") applyBrand(student.branding);
    else if (student === false) clearBrand();
  }, [student]);

  // Step 1: password validation -> server sends a code to email + mobile (mandatory 2FA). No session.
  const login = async (tenant_slug, identifier, password) => {
    const { data } = await sapi.post("/student/auth/login", { tenant_slug, identifier, password });
    return data;  // { otp_required, masked_email, masked_mobile, channels }
  };

  // Step 2: verify the code and complete login.
  const loginVerify = async (tenant_slug, identifier, otp) => {
    const { data } = await sapi.post("/student/auth/login/verify", { tenant_slug, identifier, otp });
    setStudent(data.user);
    return data.user;
  };

  const claim = async (payload) => {
    const { data } = await sapi.post("/student/auth/claim", payload);
    setStudent(data.user);
    return data;
  };

  const logout = async () => {
    try { await sapi.post("/student/auth/logout"); } catch {}
    setStudent(false);
  };

  return (
    <StudentAuthContext.Provider value={{ student, loading, login, loginVerify, claim, logout, formatApiErrorDetail }}>
      {children}
    </StudentAuthContext.Provider>
  );
}
